import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { VideoItem } from "@/types/video";
import { categories } from "@/mocks/categories";
import { videos } from "@/mocks/videos";
import { useSimulatedLoading } from "@/hooks/useSimulatedLoading";
import Seo from "@/components/feature/Seo";
import VideoCard from "@/components/feature/VideoCard";
import LoadingGrid from "@/components/feature/LoadingGrid";
import EmptyState from "@/components/feature/EmptyState";
import Pagination from "@/components/feature/Pagination";

const PAGE_SIZE = 6;

const sortOptions = [
  { key: "latest", label: "时间最新" },
  { key: "hot", label: "最热" },
  { key: "title", label: "标题排序" },
];

type SortKey = "latest" | "hot" | "title";

export default function Category() {
  const { categoryId } = useParams();
  const loading = useSimulatedLoading();
  const category = categories.find((item) => item.id === categoryId);

  const categoryVideos = useMemo<VideoItem[]>(
    () => videos.filter((video) => video.categoryId === categoryId),
    [categoryId],
  );

  const regions = useMemo(
    () => Array.from(new Set(categoryVideos.map((video) => video.region))),
    [categoryVideos],
  );
  const years = useMemo(
    () => Array.from(new Set(categoryVideos.map((video) => video.year))).sort((a, b) => b.localeCompare(a)),
    [categoryVideos],
  );
  const qualities = useMemo(
    () => Array.from(new Set(categoryVideos.map((video) => video.quality))),
    [categoryVideos],
  );

  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [quality, setQuality] = useState("全部");
  const [sort, setSort] = useState<SortKey>("latest");
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    setRegion("全部");
    setYear("全部");
    setQuality("全部");
    setSort("latest");
    setPage(1);
  }, [categoryId]);

  useEffect(() => {
    setPage(1);
  }, [region, year, quality, sort]);

  const filtered = useMemo(() => {
    const list = categoryVideos.filter((video) => {
      const matchRegion = region === "全部" || video.region === region;
      const matchYear = year === "全部" || video.year === year;
      const matchQuality = quality === "全部" || video.quality === quality;
      return matchRegion && matchYear && matchQuality;
    });
    return list.sort((a, b) => {
      if (sort === "hot") return b.views - a.views;
      if (sort === "title") return a.title.localeCompare(b.title, "zh-Hans-CN");
      return b.uploadDate.localeCompare(a.uploadDate);
    });
  }, [categoryVideos, region, year, quality, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageVideos = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const resetFilters = () => {
    setRegion("全部");
    setYear("全部");
    setQuality("全部");
    setSort("latest");
  };

  if (!category) {
    return (
      <div className="mx-auto w-full max-w-7xl px-4 py-16 md:px-6">
        <EmptyState
          icon="ri-error-warning-line"
          title="分类不存在"
          description="该视频分类可能已被移除，请返回首页重新选择。"
          actionLabel="返回首页"
          actionTo="/"
        />
      </div>
    );
  }

  const renderGroup = (label: string, options: string[], value: string, onSelect: (v: string) => void) => (
    <div className="flex flex-wrap items-start gap-2">
      <span className="mt-1.5 w-14 shrink-0 text-sm text-foreground-500">{label}</span>
      <div className="flex flex-wrap gap-2">
        {["全部", ...options].map((option) => (
          <button
            key={`${label}-${option}`}
            type="button"
            onClick={() => onSelect(option)}
            className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition-colors ${
              value === option
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-700"
            }`}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );

  const filterPanel = (
    <div className="space-y-4">
      {renderGroup("地区", regions, region, setRegion)}
      {renderGroup("年份", years, year, setYear)}
      {renderGroup("清晰度", qualities, quality, setQuality)}
    </div>
  );

  return (
    <>
      <Seo
        title={`${category.name}-高清视频资源分类 - 黄瓜视频下载`}
        description={`黄瓜视频下载${category.name}分类聚合页，${category.description}当前收录 ${categoryVideos.length} 个演示视频条目。`}
        keywords={`黄瓜视频下载,${category.name},高清视频,视频资源,${category.name}分类`}
      />

      <div className="mx-auto w-full max-w-7xl px-4 pt-6 md:px-6 md:pt-8">
        <nav className="flex flex-wrap items-center gap-1 text-xs text-foreground-500 md:text-sm" aria-label="面包屑">
          <Link to="/" className="transition-colors hover:text-primary-600">
            首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span>分类</span>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-900">{category.name}</span>
        </nav>

        <div className="mt-4 flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
            <i className={`${category.icon} text-2xl`}></i>
          </span>
          <div>
            <h1 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              {category.name}分类
            </h1>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">
              {category.description}
            </p>
            <p className="mt-2 text-xs text-foreground-500">共 {categoryVideos.length} 个演示视频条目</p>
          </div>
        </div>

        <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground-800">
              <i className="ri-filter-3-line text-primary-600"></i>
              筛选与排序
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={resetFilters}
                className="hidden items-center gap-1 whitespace-nowrap rounded-md border border-background-200 px-3 py-1.5 text-sm text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600 md:flex"
              >
                <i className="ri-refresh-line"></i>
                重置筛选
              </button>
              <button
                type="button"
                onClick={() => setFilterOpen((prev) => !prev)}
                className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-200 px-3 py-1.5 text-sm text-foreground-700 md:hidden"
              >
                <i className="ri-equalizer-line"></i>
                筛选
              </button>
            </div>
          </div>

          <div className={`mt-4 ${filterOpen ? "block" : "hidden"} md:block`}>{filterPanel}</div>

          <div className="mt-4 flex items-center gap-3 border-t border-background-100 pt-4">
            <div className="flex items-center gap-1 rounded-full border border-background-200 bg-background-50 p-1">
              {sortOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setSort(option.key as SortKey)}
                  className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm transition-colors ${
                    sort === option.key
                      ? "bg-secondary-500 text-background-50"
                      : "text-foreground-600 hover:text-foreground-950"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={resetFilters}
              className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-200 px-3 py-1.5 text-sm text-foreground-600 md:hidden"
            >
              <i className="ri-refresh-line"></i>
              重置
            </button>
          </div>
        </div>

        <div className="mt-6">
          {loading ? (
            <LoadingGrid count={6} columns="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" />
          ) : pageVideos.length > 0 ? (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 md:gap-5">
                {pageVideos.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
              <Pagination page={page} totalPages={totalPages} onChange={setPage} />
            </>
          ) : (
            <EmptyState
              icon="ri-search-eye-line"
              title="当前筛选无资源"
              description="没有找到符合当前筛选条件的视频，尝试重置筛选或切换其他条件。"
              actionLabel="重置筛选"
              onAction={resetFilters}
            />
          )}
        </div>
      </div>
    </>
  );
}